/**
 * External Imports
 */
import { useCallback, FC, Dispatch, SetStateAction, useState } from 'react';

/**
 * Nexus Imports
 */
import { NeuButton, NeuTextarea } from '@neutron/react';

/**
 * Internal Imports
 */
import { BlueChip } from '../../../shared/chips/blueChip';
import { Dropdown } from '../../../shared/dropdowns';
import { toTitleCase } from '../../../../utils/helpers';

/**
 * Style Imports
 */
import {
  BlueSpanText,
  CardTitle,
  ChipWrapper,
  GreyCardTitle,
  GreyPastQuestionTitle,
  MultiContainer,
  OptionsContainer,
  PastAnswerContent,
  PastAnswerText,
  PastQuestionTitle,
  SingleContainer
} from './Question.styles';

interface IQuestionPartProps {
  data: any;
  quesId: string;
  questionPart: any;
  setData: Dispatch<SetStateAction<{}>>;
  updateAggregatePart: (
    aggPart: any,
    questionPartId: string,
    newAnswer: any,
    questions: any
  ) => any;
  viewOnly: boolean;
  isRequired?: boolean;
  tags?: string[];
}

const QuestionPart: FC<IQuestionPartProps> = ({
  data,
  quesId,
  questionPart,
  viewOnly,
  setData,
  updateAggregatePart,
  isRequired,
  tags
}) => {
  const [openOptions, setOpenOptions] = useState(false);
  const [openScale, setOpenScale] = useState(false);

  const updateConditions = useCallback(
    (
      arr: any,
      flag: string,
      id: string,
      isMultiSelect: boolean,
      remove?: boolean
    ) => {
      if (
        arr.findIndex(
          (cond: { questionPartId: string; flag: string }) =>
            cond.questionPartId === id
        ) !== -1
      ) {
        if (flag) {
          return arr.map((cond: { questionPartId: string; flag: string[] }) =>
            cond.questionPartId === id
              ? {
                  questionPartId: cond.questionPartId,
                  flag:
                    isMultiSelect && remove
                      ? cond.flag.filter((f: string) => flag !== f)
                      : isMultiSelect
                      ? [...cond.flag, flag]
                      : [flag]
                }
              : cond
          );
        }
        return arr.filter(
          (cond: { questionPartId: string; flag: string }) =>
            cond.questionPartId !== id
        );
      }
      if (flag) {
        return [...arr, { questionPartId: id, flag: [flag] }];
      }
      return [...arr];
    },
    []
  );

  const hasMaxAggregateAnswersCount = (part: any, fromCloseParam: boolean) => {
    if (part.questionType !== 'aggregate') {
      return false;
    }
    if (part.selectedAnswer === undefined) {
      return false;
    }
    let maxAnswersCount = 1;
    const { maxAnswers = '1' } = part.config;
    if (!Number.isNaN(parseInt(maxAnswers, 10))) {
      maxAnswersCount = parseInt(maxAnswers, 10);
    }
    if (fromCloseParam) {
      maxAnswersCount -= 1;
    }
    return part.selectedAnswer.length >= maxAnswersCount;
  };

  const handleChangeAnswer = useCallback(
    (
      id: string,
      questionPartId: string,
      option: any,
      questionType: string,
      remove?: true
    ) => {
      const isMultiSelect =
        questionType === 'multi' || questionType === 'aggregate';
      const newQuestions = data.questions.map((ques: any) => {
        return id === ques.questionId
          ? {
              ...ques,
              conditions: updateConditions(
                ques.conditions,
                option.flag,
                questionPartId,
                isMultiSelect,
                remove
              ),
              questionParts: [
                ...ques.questionParts.map((part: any) => {
                  return questionPartId === part.questionPartId
                    ? {
                        ...part,
                        selectedAnswer:
                          isMultiSelect && part.selectedAnswer
                            ? remove &&
                              part.selectedAnswer?.findIndex(
                                (val: any) => val.optionId === option.optionId
                              ) !== -1
                              ? part.selectedAnswer?.filter(
                                  (val: any) => val.optionId !== option.optionId
                                )
                              : part.selectedAnswer?.findIndex(
                                  (val: any) => val.optionId === option.optionId
                                ) !== -1
                              ? [...part.selectedAnswer]
                              : [
                                  ...part.selectedAnswer,
                                  {
                                    optionId: option.optionId,
                                    value: option.value,
                                    title: option.title,
                                    charge: option.charge,
                                    flag: option.flag
                                  }
                                ]
                            : [
                                {
                                  optionId: option.optionId,
                                  value: option.value,
                                  title: option.title,
                                  charge: option.charge,
                                  flag: option.flag
                                }
                              ]
                      }
                    : part.questionType === 'aggregate' &&
                      questionType === 'single'
                    ? updateAggregatePart(
                        part,
                        questionPartId,
                        option,
                        data.questions
                      )
                    : part;
                })
              ]
            }
          : questionType === 'single'
          ? {
              ...ques,
              questionParts: ques.questionParts.map((part: any) => {
                return part.questionType === 'aggregate'
                  ? updateAggregatePart(
                      part,
                      questionPartId,
                      option,
                      data.questions
                    )
                  : part;
              })
            }
          : ques;
      });

      setData({
        ...data,
        selectedConditions:
          data.selectedConditions.findIndex(
            (cond: { questionId: string; flags: string[] }) =>
              cond.questionId === id
          ) !== -1
            ? data.selectedConditions.map(
                (c: { questionId: string; flags: string[] }) =>
                  c.questionId === id
                    ? {
                        questionId: id,
                        flags: newQuestions
                          .find((ques: any) => ques.questionId === id)
                          .conditions.reduce(
                            (
                              acc: string[],
                              cond: { questionPartId: string; flag: string }
                            ) => [...acc, ...cond.flag],
                            []
                          )
                      }
                    : c
              )
            : [
                ...data.selectedConditions,
                {
                  questionId: id,
                  flags: newQuestions
                    .find((ques: any) => ques.questionId === id)
                    .conditions.reduce(
                      (
                        acc: string[],
                        cond: { questionPartId: string; flag: string }
                      ) => [...acc, ...cond.flag],
                      []
                    )
                }
              ],
        questions: newQuestions
      });
    },
    [data]
  );

  const handleTextChange = useCallback(
    (id: string, questionPartId: string, val: string) => {
      setData({
        ...data,
        questions: data.questions.map((ques: any) => {
          return id === ques.questionId
            ? {
                ...ques,
                questionParts: [
                  ...ques.questionParts.map((part: any) => {
                    return questionPartId === part.questionPartId
                      ? {
                          ...part,
                          selectedAnswer: [
                            {
                              optionId: null,
                              value: val,
                              title: null,
                              charge: null,
                              flag: null
                            }
                          ]
                        }
                      : part;
                  })
                ]
              }
            : ques;
        })
      });
    },
    [data]
  );

  if (viewOnly) {
    return (
      <article
        className="d-flex flex-column flex-grow-1"
        id={`Round-Question-Part-${questionPart.questionPartId}`}
      >
        <section className="d-flex flex-row flex-grow-1 justify-content-between px-1 py-1">
          {questionPart.questionType === 'info' ? (
            <GreyPastQuestionTitle>
              {questionPart.question}
            </GreyPastQuestionTitle>
          ) : (
            <PastQuestionTitle>{questionPart.question}</PastQuestionTitle>
          )}
          {questionPart.questionType === 'single' &&
            questionPart.selectedAnswer &&
            questionPart.selectedAnswer.length > 0 && (
              <PastAnswerContent style={{ width: 'fit-content' }}>
                <NeuButton
                  disabled
                  kind="tertiary"
                  color="blue"
                  size="small"
                  value={toTitleCase(
                    questionPart.selectedAnswer[0].title ||
                      questionPart.selectedAnswer[0].value
                  )}
                >
                  <BlueSpanText>
                    {toTitleCase(
                      questionPart.selectedAnswer[0].title ||
                        questionPart.selectedAnswer[0].value
                    )}
                  </BlueSpanText>
                </NeuButton>
              </PastAnswerContent>
            )}
        </section>
        {questionPart.questionType !== 'single' &&
          questionPart.selectedAnswer && (
            <section className="px-1 py-1">
              {questionPart.selectedAnswer.length === 1 ? (
                <PastAnswerText className="px-1 py-1">
                  {questionPart.selectedAnswer[0].title ||
                    questionPart.selectedAnswer[0].value}
                </PastAnswerText>
              ) : questionPart.selectedAnswer &&
                questionPart.selectedAnswer.length > 1 ? (
                questionPart.selectedAnswer.length > 0 &&
                questionPart.selectedAnswer.map((ans: any = {}) => {
                  return (
                    <ChipWrapper key={ans.optionId}>
                      <BlueChip
                        icon=" "
                        key={ans.title}
                        name={ans.title}
                        removable={false}
                      />
                    </ChipWrapper>
                  );
                })
              ) : (
                ''
              )}
            </section>
          )}
      </article>
    );
  }
  return (
    <article
      id={`Round-Question-Part-${questionPart.questionPartId}`}
      className={`position-relative pr-1${
        isRequired && questionPart.questionType !== 'info'
          ? ' required-content'
          : ''
      }`}
      style={{ boxShadow: 'none', overflow: 'visible' }}
    >
      <section className="d-flex flex-row justify-content-between px-1 py-1">
        {questionPart.questionType === 'info' ? (
          <GreyCardTitle className="neu-text-gray-80 bold me-3">
            {questionPart.question}
          </GreyCardTitle>
        ) : (
          <CardTitle className="me-3">{questionPart.question}</CardTitle>
        )}
        {questionPart.questionType === 'scale' && (
          <Dropdown
            classes="pe-0"
            open={openScale}
            placeholder={
              questionPart.selectedAnswer &&
              questionPart.selectedAnswer.length === 1
                ? questionPart.selectedAnswer[0].value
                : 'Select a choice'
            }
            placeholderAlign={
              questionPart.selectedAnswer &&
              questionPart.selectedAnswer.length === 1
                ? 'right'
                : 'left'
            }
            styles={{
              minWidth: '235px',
              width: 'auto'
            }}
            width="91%"
            setOpen={setOpenScale}
          >
            {[...Array(10).keys()]
              .map(i => String(i + 1))
              .map((option: string) => (
                <div
                  aria-selected={
                    questionPart.selectedAnswer &&
                    option === questionPart.selectedAnswer[0].value
                  }
                  role="option"
                  tabIndex={0}
                  id={`Question-Option-${option}`}
                  className="dropdown-hover-item"
                  key={option}
                  onClick={(e: any) => {
                    handleChangeAnswer(
                      quesId,
                      questionPart.questionPartId,
                      {
                        optionId: `scale-${e.target.textContent}`,
                        value: e.target.textContent,
                        title: null,
                        charge: null,
                        flag: null
                      },
                      questionPart.questionType
                    );
                  }}
                  onKeyDown={(e: any) => {
                    handleChangeAnswer(
                      quesId,
                      questionPart.questionPartId,
                      {
                        optionId: `scale-${e.target.textContent}`,
                        value: e.target.textContent,
                        title: null,
                        charge: null,
                        flag: null
                      },
                      questionPart.questionType
                    );
                  }}
                >
                  {option}
                </div>
              ))}
          </Dropdown>
        )}
        {questionPart.options.length ? (
          <OptionsContainer>
            {questionPart.questionType === 'single' &&
            (questionPart.options.length <= 2 ||
              ((tags?.includes('AIDET') || tags?.includes('BSSR')) &&
                questionPart.options.length === 3)) ? (
              <SingleContainer className="d-flex justify-content-end align-items-center mx-0 pe-0">
                {questionPart.options.map((option: any) => (
                  <NeuButton
                    key={option.optionId}
                    className="mx-2"
                    value={option}
                    kind={
                      questionPart.selectedAnswer &&
                      questionPart.selectedAnswer.length > 0 &&
                      questionPart.selectedAnswer[0].value === option.value
                        ? 'primary'
                        : 'secondary'
                    }
                    color="blue"
                    size="small"
                    onClick={() =>
                      handleChangeAnswer(
                        quesId,
                        questionPart.questionPartId,
                        option,
                        questionPart.questionType
                      )
                    }
                    style={{
                      whiteSpace: window.location.pathname.includes(
                        'validation'
                      )
                        ? 'nowrap'
                        : ''
                    }}
                  >
                    {option.title || option.value}
                  </NeuButton>
                ))}
              </SingleContainer>
            ) : (
              <MultiContainer className="d-flex justify-content-end mx-0 pe-0">
                {(questionPart.questionType === 'multi' ||
                  questionPart.questionType === 'aggregate') &&
                  questionPart.selectedAnswer && (
                    <div className="d-flex flex-row flex-wrap justify-content-end w-100 mb-2 pe-0">
                      {[...questionPart.selectedAnswer]
                        .sort((a: any, b: any) =>
                          (a.title ?? '').toLowerCase() >
                          (b.title ?? '').toLowerCase()
                            ? 1
                            : -1
                        )
                        .map((ansOption: any = {}) => {
                          return (
                            <ChipWrapper
                              key={ansOption.optionId}
                              onClick={() =>
                                handleChangeAnswer(
                                  quesId,
                                  questionPart.questionPartId,
                                  ansOption,
                                  questionPart.questionType,
                                  true
                                )
                              }
                            >
                              <BlueChip
                                icon=" "
                                key={ansOption.title}
                                name={ansOption.title}
                              />
                            </ChipWrapper>
                          );
                        })}
                    </div>
                  )}
                <Dropdown
                  disabled={hasMaxAggregateAnswersCount(questionPart, false)}
                  classes="pe-0"
                  closeOnClick={
                    questionPart.questionType === 'single' ||
                    hasMaxAggregateAnswersCount(questionPart, true)
                  }
                  open={openOptions}
                  placeholder={
                    questionPart.selectedAnswer &&
                    questionPart.selectedAnswer.length === 1
                      ? questionPart.selectedAnswer[0].title ||
                        questionPart.selectedAnswer[0].value
                      : questionPart.selectedAnswer &&
                        questionPart.selectedAnswer.length > 1
                      ? `${questionPart.selectedAnswer.length} selected`
                      : (questionPart.questionType === 'multi' ||
                          questionPart.questionType === 'aggregate') &&
                        (!questionPart.selectedAnswer ||
                          (questionPart.selectedAnswer &&
                            questionPart.selectedAnswer.length === 0))
                      ? 'Select choices'
                      : 'Select a choice'
                  }
                  styles={{
                    minWidth: '325px',

                    width: 'auto'
                  }}
                  width="91%"
                  setOpen={setOpenOptions}
                >
                  {[...questionPart.options]
                    .sort((a: any, b: any) =>
                      (a.title ?? '').toLowerCase() >
                      (b.title ?? '').toLowerCase()
                        ? 1
                        : -1
                    )
                    .map((option: any) => {
                      const selected =
                        questionPart.selectedAnswer &&
                        questionPart.selectedAnswer?.some(
                          (answer: any) => option.optionId === answer.optionId
                        );
                      return (
                        <div
                          aria-selected={selected}
                          role="option"
                          tabIndex={0}
                          id={`Question-Option-${option.title}`}
                          className={`dropdown-hover-item ${
                            selected ? 'selected' : ''
                          }`}
                          key={option.optionId}
                          onClick={() => {
                            handleChangeAnswer(
                              quesId,
                              questionPart.questionPartId,
                              option,
                              questionPart.questionType
                            );
                          }}
                          onKeyDown={() => {
                            handleChangeAnswer(
                              quesId,
                              questionPart.questionPartId,
                              option,
                              questionPart.questionType
                            );
                          }}
                        >
                          {option.title || option.value}
                        </div>
                      );
                    })}
                </Dropdown>
              </MultiContainer>
            )}
          </OptionsContainer>
        ) : null}
      </section>
      {questionPart.questionType === 'text' && (
        <section>
          <NeuTextarea
            id="Question-Part-Question-Type-Text-Area"
            className="px-1 py-1"
            label="Do not include PHI"
            required
            spellCheck
            value={
              questionPart.selectedAnswer &&
              questionPart.selectedAnswer.length > 0
                ? questionPart.selectedAnswer[0].title ||
                  questionPart.selectedAnswer[0].value
                : ''
            }
            onNeuChange={(e: any) =>
              handleTextChange(
                quesId,
                questionPart.questionPartId,
                e.target?.value
              )
            }
          />
        </section>
      )}
    </article>
  );
};

export default QuestionPart;
